import FocusTrap from 'focus-trap-react';
import kebabCase from 'lodash/kebabCase';
import React, { MouseEventHandler } from 'react';
import colors from '../../../../helpers/colors';
import componentProps, { ComponentProps } from '../../../../helpers/componentProps';
import { generateKeyForElement } from '../../../../helpers/elementKeyHelper';
import Collapsible from '../../../Collapsible';
import Icon from '../../../Icon';
import { SecondaryNav, SecondaryNavItem } from '../../types';
import { useActiveItems } from '../../useActiveItems';
import useNavKeyboardControl from '../../useNavKeyboardControl';
import SecondaryNavMenu from './SecondaryNavMenu';
import {
  DesktopIconBackground,
  InlineSecondaryNav,
  NavItem,
  SecondaryMenu,
  SecondaryNavItemText,
  SecondaryNavItemWrapper,
} from './styles';

// The FocusTrap doesn't interact well with some tests, so we pass in an empty div with this id
const focusTrapFallbackId = 'test-only-fallback';

const getSecondaryMenuId = (name: string): string => `${kebabCase(name)}-menu`;

type SecondaryNavMenuItemProps = {
  navItem: SecondaryNavItem;
  isActive: boolean;
  onClick: MouseEventHandler;
};

const SecondaryNavMenuItem: React.FC<SecondaryNavMenuItemProps> = ({
  navItem: { icon, name },
  isActive,
  onClick,
}) => (
  <NavItem
    type="button"
    key={name}
    onClick={onClick}
    isActive={isActive}
    aria-expanded={isActive}
    aria-haspopup
    aria-controls={getSecondaryMenuId(name)}>
    <SecondaryNavItemText>
      <DesktopIconBackground isActive={isActive}>
        {icon && <Icon name={icon} size="x-small" color={colors.neutral08} />}
      </DesktopIconBackground>
      {name}
    </SecondaryNavItemText>
  </NavItem>
);

type SecondaryNavDesktopProps = {
  secondaryNav: SecondaryNav;
  activeItemIndex: number | null;
  setActiveItemIndex: (index: number | null) => void;
  resetNav: () => void;
  enableKeyboardNavigation?: boolean;
} & ComponentProps;

const SecondaryNavDesktop: React.FC<SecondaryNavDesktopProps> = ({
  secondaryNav: { navItems },
  resetNav,
  activeItemIndex: parentActiveItemIndex,
  setActiveItemIndex: parentSetActiveItemIndex,
  enableKeyboardNavigation,
  ...props
}) => {
  const [{ activeItemIndex }, onClickNavItem] = useActiveItems(
    parentActiveItemIndex,
    parentSetActiveItemIndex
  );
  const navListRef = useNavKeyboardControl<HTMLDivElement>();

  const onClick = (
    event: React.MouseEvent,
    navItem: SecondaryNavItem,
    index: number
  ): void => {
    if (navItem.onExpand && activeItemIndex !== index) {
      navItem.onExpand(event);
    }
    if (navItem.onCollapse && activeItemIndex === index) {
      navItem.onCollapse(event);
    }
    onClickNavItem(index)();
  };

  return (
    <FocusTrap
      active={activeItemIndex !== null}
      focusTrapOptions={{
        clickOutsideDeactivates: true,
        fallbackFocus: `#${focusTrapFallbackId}`,
      }}>
      <InlineSecondaryNav
        ref={enableKeyboardNavigation ? navListRef : undefined}
        data-testid="secondary-nav"
        {...componentProps(props)}>
        {navItems
          .slice(0, 2) // Strictly enforce two items maximum to make the menu alignment sensible (see `alignRight` in Secondary Menu below)
          .map((navItem, index) => (
            <SecondaryNavItemWrapper key={generateKeyForElement(navItem)}>
              <SecondaryNavMenuItem
                navItem={navItem}
                isActive={activeItemIndex === index}
                onClick={(event) => onClick(event, navItem, index)}
              />
              <SecondaryMenu alignRight={index === 0}>
                <Collapsible
                  isOpened={activeItemIndex === index}
                  id={getSecondaryMenuId(navItem.name)}>
                  <SecondaryNavMenu navItem={navItem} resetNav={resetNav} />
                </Collapsible>
              </SecondaryMenu>
            </SecondaryNavItemWrapper>
          ))
          .reverse()}
        <div id={focusTrapFallbackId} />
      </InlineSecondaryNav>
    </FocusTrap>
  );
};

export default SecondaryNavDesktop;
